import { useState } from 'react'
import MainLayout from '../../layouts/MainLayout'
import PageHeader from '../../components/PageHeader'
import { usePermissions } from '../../hooks/usePermissions'
import { useNavigate } from 'react-router-dom'
import { faEye } from '@fortawesome/free-solid-svg-icons'
import { entities } from '../../constants/entities'
import ListHeader from '../../components/ListHeader'
import FilterCollapse from '../../components/FilterCollapse'
import AutoCompleteFilter from '../../components/AutoCompleteFilter'
import baseService from '../../services/baseService'
import TableWithInactiveFilter from '../../components/table/TableWithInactiveFilter'
import useBaseService from '../../hooks/services/useBaseService'
import useQueryWithLoader from '../../hooks/useQueryWithLoader'
import { Box, Checkbox, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material'

const WarrantiesPage = () => {
  const navigate = useNavigate()
  const { canAccess } = usePermissions()
  const { get: baseServiceGet } = useBaseService(navigate)

  // Estado LOCAL do formulário
  const [localEquipments, setLocalEquipments] = useState([])
  const [localIncludeExpired, setLocalIncludeExpired] = useState(false)

  // Estado APLICADO (vai pra query) — incluindo paginação local
  const [equipmentIds, setEquipmentIds] = useState([])
  const [includeExpired, setIncludeExpired] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(25)

    // Estado para o filtro de Status
  const [localEquipmentStatus, setLocalEquipmentStatus] = useState('')
  const [equipmentStatus, setEquipmentStatus] = useState('')

  const {
    data: warrantiesData,
    isLoading,
    isFetching,
  } = useQueryWithLoader({
    // ✅ queryKey inclui TUDO que muda → garante refetch
        queryKey: ['warranties', equipmentIds, includeExpired, equipmentStatus, page, perPage],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.equipments.warranties.get,
        {
          page,
          perPage,
          // 🆕 manda pelo canal "filters", que o baseService preserva
          filters: {
            include_expired: includeExpired ? '1' : '0',
            ...(equipmentIds.length > 0 && {
              equipment_ids: equipmentIds.join(','),
            }),
            ...(equipmentStatus && { equipment_status: equipmentStatus }),
          },
        }
      )
      return response.result
    },
    select: (res) => ({
      warranties: (res.data ?? []).map((w) => ({
        ...w,
        warranty_situation: w.in_warranty
          ? `EM GARANTIA (${w.warranty_days_left} dias)`
          : `VENCIDA`,
        isDeleted: false,
      })),
      totalPages: res.last_page ?? 1,
    }),
  })

  const { warranties = [], totalPages = 1 } = warrantiesData || {}

  const columns = [
    { key: 'number', label: 'Tombo' },
    { key: 'name', label: 'Descrição', width: '22%' },
    { key: 'serial_number', label: 'Nº de Série' },
    { key: 'product', label: 'Produto', width: '18%' },
    { key: 'status', label: 'Status Equipamento' },
    { key: 'warranty_date_formatted', label: 'Data da Garantia' },
    { key: 'warranty_situation', label: 'Situação da Garantia' },
  ]

  const actions = [
    {
      id: 'details',
      icon: faEye,
      title: 'Ver Detalhes',
      buttonClass: 'btn-info',
      permission: 'Ver equipamentos',
      onClick: (w) => navigate(`/aluguel/equipamentos/detalhes/${w.id}`),
      show: () => canAccess('Ver equipamentos'),
    },
  ]

    const handleApplyFilters = (e) => {
    e?.preventDefault() // impede o reload da página
    const ids = (Array.isArray(localEquipments) ? localEquipments : [])
      .map((opt) => opt?.value)
      .filter(Boolean)
    
    setEquipmentIds(ids)
    setIncludeExpired(localIncludeExpired)
    setEquipmentStatus(localEquipmentStatus) // 🆕 ADICIONADO: aplica o status
    setPage(1)
  }

    const handleClearFilters = (e) => {
    e?.preventDefault()
    setLocalEquipments([])
    setLocalIncludeExpired(false)
    setLocalEquipmentStatus('') // 🆕 ADICIONADO: limpa o status local
    
    setEquipmentIds([])
    setIncludeExpired(false)
    setEquipmentStatus('') // 🆕 ADICIONADO: limpa o status aplicado
    setPage(1)
  }

    const filterFields = [
    {
      label: 'Equipamento (Tombo/Descrição)',
      component: (
        <AutoCompleteFilter
          service={baseService}
          columnDataBase={['number', 'name']}
          extraFilters={{ is_completed: { $eq: 1 } }}
          model="equipment"
          value={localEquipments}
          onChange={(selected) => setLocalEquipments(selected || [])}
          placeholder="Filtre pelos equipamentos"
          isMulti
          isClearable
        />
      ),
    },
    // 🆕 NOVO FILTRO: Status Equipamento
    {
      label: 'Status Equipamento',
      component: (
        <FormControl fullWidth size="small">
          <InputLabel id="status-equip-label">Status</InputLabel>
          <Select
            labelId="status-equip-label"
            value={localEquipmentStatus}
            label="Status"
            onChange={(e) => setLocalEquipmentStatus(e.target.value)}
          >
            <MenuItem value=""><em>Todos</em></MenuItem>
            <MenuItem value="ALUGADO">Alugado</MenuItem>
            <MenuItem value="DISPONÍVEL PARA LOCAÇÃO">Em Estoque</MenuItem>
            <MenuItem value="EM MANUTENÇÃO">Em Manutenção</MenuItem>
            <MenuItem value="AGUARDANDO CARREGAMENTO">Aguardando Carregamento</MenuItem>
          </Select>
        </FormControl>
      ),
    },
    {
      gridSize: '3',
      component: (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}>
          <Typography>Incluir garantias vencidas</Typography>
          <Checkbox
            className="m-0"
            checked={localIncludeExpired}
            onChange={(e) => setLocalIncludeExpired(e.target.checked)}
          />
        </Box>
      ),
    },
  ]

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Garantias"
        showBackButton={true}
        backUrl="/dashboard"
      />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={filterFields}
          onSubmit={handleApplyFilters}
          onClear={handleClearFilters}
          defaultOpen={true}
        />

        <ListHeader
          title="Itens em Garantia"
          buttonIsNotVisible={true}
          canAccess={canAccess}
        />

        <TableWithInactiveFilter
          data={warranties}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={page - 1}
          onPageChange={(newPage) => setPage(newPage + 1)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(event) => {
            setPerPage(+event.target.value)
            setPage(1)
          }}
          filterValues={{}}
          localFilterValues={{}}
          applyUrlFilters={() => {}}
          type="default"
          isLoading={isLoading}
          isFetching={isFetching}
        />
      </div>
    </MainLayout>
  )
}

export default WarrantiesPage
